import React from "react";
import { Modal, Button, Divider, Typography, Space } from "antd";
import { CheckCircleTwoTone, SendOutlined } from "@ant-design/icons";
const { Title, Text } = Typography;

const PatientDetailsModal = ({
  visible,
  patient,
  onClose,
  currentMedecin,
  downloadFile,
  formatTeethNumbers,
  formatArcadeText,
  formatRapportAP,
  formatEspacementText,
  onSendAligners,
  onShowTPCheck,
}) => {
  if (!patient) return null;

  const isOrthodontiste = currentMedecin?.role?.toLowerCase() === 'orthodontiste';

  return (
    <Modal
      title={`Patient: ${patient.name} ${patient.surname || ''}`}
      visible={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
    >
      <div style={{ position: 'relative', marginBottom: '24px' }}>
        <div style={{ position: 'absolute', top: 0, right: 0 }}>
          {patient.alignersShippedDate ? (
            <Button
              type="primary"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              disabled
            >
              Aligners expédié
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={onSendAligners}
              disabled={patient.status !== 3}
            >
              Envoyer aligners
            </Button>
          )}
          <Divider type="vertical" style={{ height: '100%' }} />
          <Button 
            type="primary" 
            onClick={onShowTPCheck}
          >
            TPCheck
          </Button>
        </div>
      </div>
      <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
        {/* Photos Section */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={4}>Photos</Title>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {[1, 2, 3, 4, 6, 7, 8, 9, 10, 11].map(num => {
              const imgKey = `img${num}`;
              return patient.images && patient.images[imgKey] ? (
                <div key={imgKey} style={{ marginBottom: '16px' }}>
                  <img 
                    src={patient.images[imgKey]} 
                    alt={`Photo ${num}`} 
                    style={{ maxWidth: '200px', maxHeight: '200px' }} 
                  />
                  <div>
                    <Button 
                      // You may want to pass downloadFile as a prop
                      onClick={() => downloadFile(patient.images[imgKey], `photo_${num}.jpg`)}
                      size="small"
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ) : null;
            })}
            {(!patient.images || !Object.keys(patient.images).some(key => {
              const num = parseInt(key.substring(3));
              return (num >= 1 && num <= 4) || (num >= 6 && num <= 11);
            })) && (
              <Text>No photos available</Text>
            )}
          </div>
        </div>
        {/* Scans Section */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={4}>Scans</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            {patient.images && patient.images.img12 && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Scan 1</Text>
                <div>
                  <Button 
                    onClick={() => downloadFile(
                      patient.images.img12, 
                      `scan_1.${patient.images.img12.toLowerCase().includes('.ply') ? 'ply' : 'stl'}`
                    )}
                  >
                    Download Scan 1
                  </Button>
                </div>
              </div>
            )}
            {patient.images && patient.images.img13 && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Scan 2</Text>
                <div>
                  <Button 
                    onClick={() => downloadFile(
                      patient.images.img13, 
                      `scan_2.${patient.images.img13.toLowerCase().includes('.ply') ? 'ply' : 'stl'}`
                    )}
                  >
                    Download Scan 2
                  </Button>
                </div>
              </div>
            )}
            {(!patient.images || (!patient.images.img12 && !patient.images.img13)) && (
              <Text>No scans available</Text>
            )}
          </Space>
        </div>
        {/* Prescription Section */}
        <div>
          <Title level={4}>Prescription Details</Title>
          <div style={{ padding: '16px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Basic Patient Info */}
              <div>
                <Text strong>Patient Name:</Text> {patient.name} {patient.surname || ''}
              </div>
              <div>
                <Text strong>Gender:</Text> {patient.gender} 
              </div>
              <div>
                <Text strong>Category:</Text> {patient.category} 
              </div>
              <div>
                <Text strong>Date of birth:</Text> {patient.dateOfBirth} 
              </div>
              <div>
                <Text strong>Status:</Text> {/* You may want to pass getStatusText as a prop */}
              </div>
              {/* TPCheck Info */}
              {patient.tpcheckLink && (
                <div>
                  <Text strong>TPCheck Link:</Text> 
                  <a href={patient.TPCheck} target="_blank" rel="noopener noreferrer">
                    {patient.TPCheck}
                  </a>
                </div>
              )}
              {patient.alignersNumber !== undefined && (
                <div>
                  <Text strong>Number of Aligners:</Text> {patient.alignersNumber}
                </div>
              )}
              {patient.changeRhythm !== undefined && (
                <div>
                  <Text strong>Change Rhythm (days):</Text> {patient.changeRhythm}
                </div>
              )}
              {patient.pack !== undefined && (
                <div>
                  <Text strong>Pack:</Text> {patient.pack}
                </div>
              )}
              {patient.price !== undefined && (
                <div>
                  <Text strong>Price:</Text> {patient.price} €
                </div>
              )}
              {/* Detailed Prescription Info */}
              {patient.prescription && (
                  <>
                    <Divider orientation="left">Detailed Prescription</Divider>
                    
                    {/* 1. Arcade(s) à traiter */}
                    {patient.prescription.arcade && (
                      <div>
                        <Text strong>1. Arcade(s) à traiter:</Text>
                        <div style={{ marginLeft: '20px' }}>
                          {formatArcadeText(
                            patient.prescription.arcade,
                            patient.prescription.maxillaireOption,
                            patient.prescription.mandibulaireOption
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* 2. Restrictions des mouvements dentaires */}
                    {patient.prescription.restrictions && (
                      <div>
                        <Text strong>2. Restrictions des mouvements dentaires:</Text>
                        <div style={{ marginLeft: '20px' }}>
                          {patient.prescription.restrictions === 'none' 
                            ? 'Aucune restriction - Déplacer toutes les dents'
                            : patient.prescription.restrictions === 'some' 
                              ? `Ne pas déplacer: ${formatTeethNumbers(patient.prescription.restrictionsTeeth || patient.prescription.selectedTeeth)}`
                              : 'Non spécifié'
                          }
                        </div>
                      </div>
                    )}
                    
                    {/* 3. Taquets */}
                    {patient.prescription.taquets && (
                      <div>
                        <Text strong>3. Taquets:</Text>
                        <div style={{ marginLeft: '20px' }}>
                          {patient.prescription.taquets === 'none'
                            ? 'Placer des taquets si nécessaire'
                            : patient.prescription.taquets === 'some'
                              ? `Ne pas placer de taquets sur: ${formatTeethNumbers(patient.prescription.taquetsTeeth)}`
                              : 'Non spécifié'
                          }
                        </div>
                      </div>
                    )}
                    
                    {isOrthodontiste ? (
                      <>
                        {/* 4. Rapport A-P */}
                        {patient.prescription.rapportAP && (
                          <div>
                            <Text strong>4. Rapport antéro-postérieur:</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                              {formatRapportAP(patient.prescription.rapportAP)}
                            </div>
                          </div>
                        )}
                        
                        {/* 5. Overjet */}
                        {patient.prescription.overjet && (
                          <div>
                            <Text strong>5. Overjet:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              {patient.prescription.overjet || 'Non spécifié'}
                            </div>
                          </div>
                        )}
                        
                        {/* 6. Overbite */}
                        {patient.prescription.overbite && (
                          <div>
                            <Text strong>6. Overbite:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>{patient.prescription.overbite || 'Non spécifié'}</div>
                              
                              {patient.prescription.maxillaireOverbite?.selected && (
                                <div>
                                  Maxillaire:
                                  {patient.prescription.maxillaireOverbite.egressionAnterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Égression des dents antérieures</div>}
                                  {patient.prescription.maxillaireOverbite.ingressionPosterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Ingression des dents postérieures</div>}
                                </div>
                              )}
                              
                              {patient.prescription.mandibulaireOverbite?.selected && (
                                <div>
                                  Mandibulaire:
                                  {patient.prescription.mandibulaireOverbite.egressionAnterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Égression des dents antérieures</div>}
                                  {patient.prescription.mandibulaireOverbite.ingressionPosterieure && 
                                    <div style={{ marginLeft: '10px' }}>- Ingression des dents postérieures</div>}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 7. Bite Ramps */}
                        {patient.prescription.biteRamps && (
                          <div>
                            <Text strong>7. Bite Ramps:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>{patient.prescription.biteRamps}</div>
                              {Array.isArray(patient.prescription.biteRampsOptions) && 
                               patient.prescription.biteRampsOptions.length > 0 && (
                                <div>Options: {patient.prescription.biteRampsOptions.join(', ')}</div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 8. Milieux */}
                        {patient.prescription.milieux && (
                          <div>
                            <Text strong>8. Milieux inter-incisifs:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>
                                {patient.prescription.milieux === 'realiser' 
                                  ? 'Réaliser les autres objectifs et j\'évaluerai les milieux inter-incisifs'
                                  : patient.prescription.milieux === 'maintenir'
                                    ? 'Maintenir les milieux inter-incisifs d\'origine'
                                    : patient.prescription.milieux === 'ameliorer'
                                      ? 'Améliorer les milieux inter-incisifs par IPR'
                                      : 'Non spécifié'
                                }
                              </div>
                              {patient.prescription.milieuxOptions && patient.prescription.milieuxOptions.length > 0 && (
                                <div>
                                  {patient.prescription.milieuxOptions.includes('maxillaire') && (
                                    <div>
                                      Maxillaire:
                                      {patient.prescription.milieuxOptions.includes('maxillaireDirection-droite') && ' vers la droite'}
                                      {patient.prescription.milieuxOptions.includes('maxillaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                  {patient.prescription.milieuxOptions.includes('mandibulaire') && (
                                    <div>
                                      Mandibulaire:
                                      {patient.prescription.milieuxOptions.includes('mandibulaireDirection-droite') && ' vers la droite'}
                                      {patient.prescription.milieuxOptions.includes('mandibulaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 9. Articulé croisé postérieur */}
                        {patient.prescription.articule && (
                          <div>
                            <Text strong>9. Articulé croisé postérieur:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              {patient.prescription.articule === 'none' ? 'Ne pas corriger' :
                               patient.prescription.articule === 'some' ? 'Corriger' :
                               'Non spécifié'}
                            </div>
                          </div>
                        )}
                        
                        {/* 10. Espacement et Encombrement */}
                        {(patient.prescription.espacement || 
                          patient.prescription.expansion || 
                          patient.prescription.expansion2 || 
                          patient.prescription.vestibuloversion ||
                          patient.prescription.vestibuloversion2 ||
                          patient.prescription.ripAnt ||
                          patient.prescription.ripAnt2 ||
                          patient.prescription.ripPostDroite ||
                          patient.prescription.ripPostDroite2 ||
                          patient.prescription.ripPostGauche ||
                          patient.prescription.ripPostGauche2 ||
                          patient.prescription.extractions) && (
                          <div>
                            <Text strong>10. Espacement et Encombrement (DDM):</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                              {formatEspacementText(patient.prescription)}
                            </div>
                          </div>
                        )}
                        
                        {/* 11. Instructions Spéciales */}
                        {patient.prescription.specialInstructions && (
                          <div>
                            <Text strong>11. Instructions Spéciales:</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-wrap' }}>
                              {typeof patient.prescription.specialInstructions === 'string' 
                                ? patient.prescription.specialInstructions 
                                : 'Aucune instruction spéciale'}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* 4. Milieux */}
                        {patient.prescription.milieux && (
                          <div>
                            <Text strong>4. Milieux inter-incisifs:</Text>
                            <div style={{ marginLeft: '20px' }}>
                              <div>
                                {patient.prescription.milieux === 'realiser' 
                                  ? 'Réaliser les autres objectifs et j\'évaluerai les milieux inter-incisifs'
                                  : patient.prescription.milieux === 'maintenir'
                                    ? 'Maintenir les milieux inter-incisifs d\'origine'
                                    : patient.prescription.milieux === 'ameliorer'
                                      ? 'Améliorer les milieux inter-incisifs par IPR'
                                      : 'Non spécifié'
                                }
                              </div>
                              {patient.prescription.milieuxOptions && patient.prescription.milieuxOptions.length > 0 && (
                                <div>
                                  {patient.prescription.milieuxOptions.includes('maxillaire') && (
                                    <div>
                                      Maxillaire:
                                      {patient.prescription.milieuxOptions.includes('maxillaireDirection-droite') && ' vers la droite'}
                                      {patient.prescription.milieuxOptions.includes('maxillaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                  {patient.prescription.milieuxOptions.includes('mandibulaire') && (
                                    <div>
                                      Mandibulaire:
                                      {patient.prescription.milieuxOptions.includes('mandibulaireDirection-droite') && ' vers la droite'}
                                      {patient.prescription.milieuxOptions.includes('mandibulaireDirection-gauche') && ' vers la gauche'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* 5. Espacement et Encombrement */}
                        {(patient.prescription.espacement || 
                          patient.prescription.expansion || 
                          patient.prescription.expansion2 || 
                          patient.prescription.vestibuloversion ||
                          patient.prescription.vestibuloversion2 ||
                          patient.prescription.ripAnt ||
                          patient.prescription.ripAnt2 ||
                          patient.prescription.ripPostDroite ||
                          patient.prescription.ripPostDroite2 ||
                          patient.prescription.ripPostGauche ||
                          patient.prescription.ripPostGauche2 ||
                          patient.prescription.extractions) && (
                          <div>
                            <Text strong>5. Espacement et Encombrement (DDM):</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                              {formatEspacementText(patient.prescription)}
                            </div>
                          </div>
                        )}
                        
                        {/* 6. Instructions Spéciales */}
                        {patient.prescription.specialInstructions && (
                          <div>
                            <Text strong>6. Instructions Spéciales:</Text>
                            <div style={{ marginLeft: '20px', whiteSpace: 'pre-wrap' }}>
                              {typeof patient.prescription.specialInstructions === 'string' 
                                ? patient.prescription.specialInstructions 
                                : 'Aucune instruction spéciale'}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {/* Notes */}
                {patient.notes && (
                  <div>
                    <Text strong>Notes:</Text>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{patient.notes}</div>
                  </div>
                )}
                

                {patient.tpcheckDate && (
                  <div>
                    <Text strong>TPCheck Date:</Text> {new Date(patient.tpcheckDate).toLocaleDateString('fr-FR')}
                  </div>
                )}
                
                {patient.alignersShippedDate && (
                  <div>
                    <Text strong>Aligners Shipped Date:</Text> {new Date(patient.alignersShippedDate).toLocaleDateString('fr-FR')}
                  </div>
                )}
            </Space>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PatientDetailsModal; 